import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';

/** Student portal — view profile (read-only) and edit bio + photo. */
@customElement('cadence-portal-profile')
export class CadencePortalProfile extends LitElement {
  @state() private me: any = null;
  @state() private bio = '';
  @state() private photoUrl = '';
  @state() private loading = true;
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.6rem; }
    .layout { display: grid; grid-template-columns: 200px 1fr; gap: var(--space-5); align-items: start; }
    @media (max-width: 720px) { .layout { grid-template-columns: 1fr; } }
    .photo { display: flex; flex-direction: column; align-items: center; gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: var(--space-4); }
    .field .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .field .v { font-weight: 600; }
    textarea {
      width: 100%; min-height: 90px; font: inherit; padding: 10px 12px; border-radius: var(--radius-md);
      border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-primary); resize: vertical; box-sizing: border-box;
    }
    textarea:focus { outline: none; border-color: var(--color-primary); }
    .editable { display: grid; gap: 12px; margin-top: var(--space-4); }
    label { font-size: 0.85rem; font-weight: 600; color: var(--color-text-secondary); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  private async load() {
    this.loading = true;
    try {
      this.me = await api.get<any>('/portal/profile');
      this.bio = this.me.bio ?? '';
      this.photoUrl = this.me.profile_photo_url ?? '';
    } finally { this.loading = false; }
  }

  private async save() {
    this.saving = true;
    try {
      await api.put('/portal/profile', { bio: this.bio });
      showToast(t('portal.toast.saved'), 'success');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  /** The photo is stored server-side on upload; just sync local state. */
  private onPhoto(url: string) {
    this.photoUrl = url;
    if (this.me) this.me = { ...this.me, profile_photo_url: url };
    showToast(t('portal.toast.photoUpdated'), 'success');
  }

  private field(label: string, value: unknown) {
    return html`<div class="field"><div class="k">${label}</div><div class="v">${value || '—'}</div></div>`;
  }

  render() {
    if (this.loading || !this.me) return html`<cadence-loading></cadence-loading>`;
    const m = this.me;
    return html`
      <h1>${t('common.portal.profileTitle')}</h1>
      <div class="layout">
        <div class="photo">
          <cadence-photo-upload
            endpoint="/portal/profile/photo"
            name=${`${m.first_name} ${m.last_name}`}
            src=${this.photoUrl || ''}
            size="120"
            @photo-uploaded=${(e: CustomEvent) => this.onPhoto(e.detail.url)}
          ></cadence-photo-upload>
        </div>
        <div>
          <div class="grid">
            ${this.field(t('portal.profile.name'), `${m.first_name ?? ''} ${m.last_name ?? ''}`)}
            ${this.field(t('portal.profile.email'), m.email)}
            ${this.field(t('portal.profile.dateOfBirth'), m.date_of_birth)}
            ${this.field(t('portal.profile.phone'), m.phone)}
            ${this.field(t('portal.profile.emergencyContact'), m.emergency_contact_name)}
            ${this.field(t('portal.profile.emergencyPhone'), m.emergency_contact_phone)}
            ${this.field(t('portal.profile.medicalNotes'), m.medical_notes)}
          </div>
          <div class="editable">
            <div>
              <label>${t('common.portal.bio')}</label>
              <textarea placeholder=${t('common.portal.bioPlaceholder')} .value=${this.bio}
                @input=${(e: Event) => (this.bio = (e.target as HTMLTextAreaElement).value)}></textarea>
            </div>
            <div><cadence-button ?loading=${this.saving} @click=${() => this.save()}>${t('common.portal.saveBio')}</cadence-button></div>
          </div>
        </div>
      </div>
    `;
  }
}
